import { Fragment } from 'react'

import { ChevronRight } from '@/assets/icons'

import type { SettingsSection, SettingsSectionId } from './settings-modal-types'

/** Fallback when no role-aware labeler is supplied: label the first group "Settings", none after. */
const defaultGroupLabel = (group: number): string | null => (group === 1 ? 'Settings' : null)

type SettingsNavListProps = {
  /** Sections to render, in order. Items are grouped by `group` with a divider between groups. */
  sections: SettingsSection[]
  /** Fired when the user taps a section row to drill into it. */
  onSelect: (id: SettingsSectionId) => void
  /** Header label for a group (e.g. "Personal settings", the org name). `null` renders no header. */
  groupLabel?: (group: number) => string | null
}

/**
 * Mobile settings navigation: a full-width, tappable list that drills into a section view.
 *
 * Presentational only. Unlike the desktop {@link SettingsNav} sidebar (select-in-place), each row
 * here is a full-width button with a trailing chevron, following the `ProviderOptionRow` pattern.
 * Each group gets a header label (from `groupLabel`) and a divider separates adjacent groups.
 */
export function SettingsNavList({ sections, onSelect, groupLabel = defaultGroupLabel }: SettingsNavListProps) {
  return (
    <nav aria-label="Settings" className="flex flex-col gap-1 px-2 py-2">
      {sections.map((section, index) => {
        const previous = sections[index - 1]
        const startsNewGroup = previous === undefined || previous.group !== section.group
        const header = startsNewGroup ? groupLabel(section.group) : null
        const Icon = section.icon

        return (
          <Fragment key={section.id}>
            {startsNewGroup && index > 0 && <div className="my-2 border-t border-border-default" />}
            {header && <p className="px-2 py-2 text-xs font-semibold uppercase tracking-wide text-brand-neutral-500">{header}</p>}
            <button
              type="button"
              onClick={() => onSelect(section.id)}
              className="flex h-12 w-full items-center gap-3 rounded-md px-2 text-left text-sm font-medium text-brand-neutral-700 transition-colors hover:bg-surface-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
            >
              <Icon className="size-4.5 shrink-0" aria-hidden="true" />
              <span className="min-w-0 flex-1 truncate">{section.label}</span>
              <ChevronRight className="size-5 shrink-0 text-brand-neutral-500" aria-hidden="true" />
            </button>
          </Fragment>
        )
      })}
    </nav>
  )
}
